import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { getWalletBalances, getCurrencies, getExchangeRates, convertCurrency, getFavoritePairs, addFavoritePair, removeFavoriteByPair } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, ArrowRight, Search, Loader2, TrendingUp, TrendingDown, Wifi, WifiOff, Star, StarOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n';
import RateAlerts from '../components/RateAlerts';

const Exchange = () => {
  const { t } = useTranslation();
  const [currencies, setCurrencies] = useState([]);
  const [balances, setBalances] = useState([]);
  const [rates, setRates] = useState({});
  const [rateSource, setRateSource] = useState('static');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBase, setSelectedBase] = useState('USD');
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      setLoadError(null);
      try {
        const [currenciesRes, balancesRes, ratesRes, favoritesRes] = await Promise.all([
          getCurrencies(),
          getWalletBalances(),
          getExchangeRates(selectedBase),
          getFavoritePairs().catch(() => ({ data: [] }))
        ]);
        setCurrencies(currenciesRes.data || []);
        setBalances(balancesRes.data || []);
        setRates(ratesRes.data?.rates || {});
        setRateSource(ratesRes.data?.source || 'static');
        setFavorites(favoritesRes.data || []);
      } catch (error) {
        logger.error('Error fetching data:', error);
        setLoadError('Failed to load exchange data. Please try again.');
        // Set fallback data so page still renders
        if (currencies.length === 0) {
          setCurrencies([
            { code: 'USD', symbol: '$', rate: 1 },
            { code: 'EUR', symbol: '€', rate: 0.85 },
            { code: 'GBP', symbol: '£', rate: 0.73 },
            { code: 'JPY', symbol: '¥', rate: 158.83 }
          ]);
        }
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, [selectedBase]);

  const isFavorite = (from, to) => {
    return favorites.some(f => f.from_currency === from && f.to_currency === to);
  };

  const toggleFavorite = async (from, to) => {
    try {
      if (isFavorite(from, to)) {
        await removeFavoriteByPair(from, to);
        setFavorites(favorites.filter(f => !(f.from_currency === from && f.to_currency === to)));
        toast.success(`Removed ${from}/${to} from favorites`);
      } else {
        const response = await addFavoritePair(from, to);
        setFavorites([...favorites, response.data.favorite]);
        toast.success(`Added ${from}/${to} to favorites`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update favorites');
    }
  };

  const getBalance = (currency) => {
    return balances.find(b => b.currency === currency)?.balance || 0;
  };

  const calculateConversion = () => {
    const amt = parseFloat(amount) || 0;
    if (!rates[fromCurrency] || !rates[toCurrency]) return 0;
    const rate = rates[toCurrency] / rates[fromCurrency];
    return (amt * rate).toFixed(2);
  };

  const calculateFee = () => {
    const amt = parseFloat(amount) || 0;
    return (amt * 0.025).toFixed(2); // 2.5% fee
  };

  const getConversionRate = () => {
    if (!rates[fromCurrency] || !rates[toCurrency]) return 0;
    return (rates[toCurrency] / rates[fromCurrency]).toFixed(6);
  };

  const handleExchange = async () => {
    const amt = parseFloat(amount);
    
    if (!amt || amt <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (fromCurrency === toCurrency) {
      toast.error('Cannot convert to the same currency');
      return;
    }

    if (amt > getBalance(fromCurrency)) {
      toast.error('Insufficient funds');
      return;
    }

    setLoading(true);
    try {
      const result = await convertCurrency(fromCurrency, toCurrency, amt);
      toast.success(`Exchanged ${amt} ${fromCurrency} to ${result.data.to_amount} ${toCurrency}`);
      
      // Reset and refresh
      setAmount('');
      const balancesRes = await getWalletBalances();
      setBalances(balancesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Exchange failed');
    } finally {
      setLoading(false);
    }
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const filteredCurrencies = currencies.filter(c => 
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      {pageLoading ? (
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="exchange-loading">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">{t('common.loading') || 'Loading exchange rates...'}</p>
          </div>
        </div>
      ) : loadError ? (
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="exchange-error">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <RefreshCw className="w-8 h-8 text-destructive" />
            </div>
            <p className="text-destructive font-medium">{loadError}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      ) : (
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="exchange-container"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">{t('nav.exchange')}</h1>
            <p className="text-muted-foreground">{t('exchange.currencies')}</p>
          </div>
          <Badge variant={rateSource === 'live' ? 'default' : 'secondary'} className="gap-1">
            {rateSource === 'live' ? (
              <>
                <Wifi className="w-3 h-3" />
                {t('exchange.liveRates')}
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3" />
                {t('exchange.staticRates')}
              </>
            )}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Exchange Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Currency Exchange</CardTitle>
                <CardDescription>Exchange at competitive rates with a 2.5% fee</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* From Currency */}
                <div className="space-y-2">
                  <Label>From</Label>
                  <div className="flex gap-3">
                    <Select value={fromCurrency} onValueChange={setFromCurrency}>
                      <SelectTrigger className="w-32 rounded-xl" data-testid="from-currency-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {(balances.length > 0 ? balances : currencies).map((item) => (
                          <SelectItem key={item.currency || item.code} value={item.currency || item.code}>
                            {item.symbol} {item.currency || item.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1">
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="h-12 rounded-xl text-lg"
                        data-testid="exchange-amount-input"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Available: ${getBalance(fromCurrency).toFixed(2)} {fromCurrency}
                  </p>
                </div>

                {/* Swap Button */}
                <div className="flex justify-center">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="rounded-full h-12 w-12"
                    onClick={swapCurrencies}
                    data-testid="swap-currencies-btn"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </Button>
                </div>

                {/* To Currency */}
                <div className="space-y-2">
                  <Label>To</Label>
                  <div className="flex gap-3">
                    <Select value={toCurrency} onValueChange={setToCurrency}>
                      <SelectTrigger className="w-32 rounded-xl" data-testid="to-currency-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.length > 0 ? currencies.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.symbol} {c.code}
                          </SelectItem>
                        )) : (
                          <SelectItem value="EUR">€ EUR</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <div className="flex-1 h-12 bg-muted rounded-xl flex items-center px-4">
                      <span className="text-lg font-mono" data-testid="converted-amount">
                        {calculateConversion()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Rate Info */}
                {amount && parseFloat(amount) > 0 && (
                  <div className="bg-muted rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Exchange Rate</span>
                      <span className="font-mono">1 {fromCurrency} = {getConversionRate()} {toCurrency}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Exchange Fee (2.5%)</span>
                      <span className="font-mono text-orange-500">-${calculateFee()} {fromCurrency}</span>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleExchange}
                  disabled={loading || !amount || parseFloat(amount) <= 0 || fromCurrency === toCurrency}
                  className="w-full h-12 rounded-xl"
                  data-testid="exchange-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Exchange Now
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
            
            {/* Rate Alerts */}
            <RateAlerts currencies={currencies} />
          </div>

          {/* Exchange Rates */}
          <div className="space-y-6">
            <Card className="h-fit">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg">Live Rates</CardTitle>
                  <Select value={selectedBase} onValueChange={setSelectedBase}>
                    <SelectTrigger className="w-24 h-8 text-xs" data-testid="base-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['USD', 'EUR', 'GBP', 'JPY'].map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search currencies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9 rounded-lg"
                    data-testid="search-currencies-input"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-border">
                    {/* Sort favorites first, then by code */}
                    {[...filteredCurrencies]
                      .sort((a, b) => {
                        const aFav = isFavorite(selectedBase, a.code);
                        const bFav = isFavorite(selectedBase, b.code);
                        if (aFav && !bFav) return -1;
                        if (!aFav && bFav) return 1;
                        return a.code.localeCompare(b.code);
                      })
                      .map((currency) => (
                      <div 
                        key={currency.code}
                        className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                        data-testid={`rate-${currency.code}`}
                      >
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(selectedBase, currency.code);
                            }}
                            data-testid={`favorite-${currency.code}`}
                          >
                            {isFavorite(selectedBase, currency.code) ? (
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            ) : (
                              <StarOff className="w-4 h-4 text-muted-foreground" />
                            )}
                          </Button>
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-mono text-xs font-bold">
                            {currency.symbol}
                          </div>
                          <span 
                            className="font-medium cursor-pointer hover:text-primary"
                            onClick={() => setToCurrency(currency.code)}
                          >
                            {currency.code}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm">
                            {rates[currency.code]?.toFixed(4) || '-'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            per {selectedBase}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>
      )}
    </Layout>
  );
};

export default Exchange;
